<template>
  <div
    :class="{
      'search-box-fi': true,
      'search-box-fi--desktop': isDesktop,
      'search-box-fi--active': isFocused || isSearchSuggestions,
    }"
  >
    <text-input-fi
      :id="id"
      ref="input"
      :model-value="query"
      class="search-box-fi__input no-highlight"
      name="search"
      :label="isDesktop ? $dict.get('search.Placeholder') : undefined"
      :placeholder="isDesktop ? undefined : $dict.get('search.Placeholder')"
      :icon="isDesktop ? 'search' : undefined"
      :clear="isDesktop"
      type="search"
      data-test="search-input"
      white
      @update:model-value="change"
      @focus="onFocus"
      @blur="onBlur"
      @keyup-enter="keyupEnter"
      @clear="clearSearch"
    />
  </div>
</template>

<script setup lang="ts">
import { TextInputFi } from 'atoms';
import { computed, ref, useTemplateRef } from 'vue';
import { useStore } from 'vuex';

import { useSearchLocation } from '@/components/search/searchLocationComposable';
import { useBreakpoints } from '@/composables/breakpoint';
import { useSearchHistory } from '@/composables/searchHistory';

defineOptions({
  name: 'SearchBoxFi',
});

const emit = defineEmits<{
  clear: [value: void];
}>();

withDefaults(
  defineProps<{
    isDesktop?: boolean;
    id?: string;
  }>(),
  {
    id: 'search-box',
  },
);

const store = useStore();
const { getSearchLocation } = useSearchLocation();
const { isSM } = useBreakpoints();
const { setSearchTerm } = useSearchHistory();

const isFocused = ref(false);
const inputRef = useTemplateRef('input');

const query = computed(() => store.getters['search/getQuery']);
const productRedirectUrl = computed(() => store.getters['search/productRedirectUrl']);
const isSearchSuggestions = computed(() => store.getters['navigation/getShowSearchSuggest']);
const isSearchPage = computed(() => store.getters['navigation/isSearch']);

const clearSearch = () => {
  emit('clear');
  setTimeout(() => {
    store.commit('navigation/SHOWFLYOUT', false);
    store.dispatch('navigation/showSearchSuggest', false);
    store.commit('search/SET_SEARCH_SUGGESTIONS', { suggestions: [], query: '' });
  }, 350);
};

const keyupEnter = (queryParam: string) => {
  if (productRedirectUrl.value === null) {
    store.commit('navigation/SHOWFLYOUT', false);
    store.dispatch('navigation/showSearchSuggest', false);
    store.dispatch('search/setQuery', '');
    return;
  }

  setSearchTerm(queryParam);

  if (!productRedirectUrl.value) {
    store.dispatch('search/setQueryEnter', true);
  } else {
    // This redirect is triggered only if the search query is finished and a redirect url is set.
    // Otherwise the redirect to the product page is triggered within store/search
    window.location.href = productRedirectUrl.value;
    return;
  }

  const newQuery = queryParam.trim();
  if (isSearchPage.value) {
    store.commit('navigation/SHOWFLYOUT', false);
    store.dispatch('navigation/showSearchSuggest', false);
    store.commit('search/SET_SEARCH_SUGGESTIONS', { suggestions: newQuery, query: newQuery });
    return;
  }
  if (newQuery === '') {
    return;
  }
  window.location.href = getSearchLocation(newQuery);
};

const focus = () => {
  inputRef.value?.focus();
};

const onFocus = () => {
  if (!isSM.value) {
    return;
  }
  isFocused.value = true;

  if (!store.getters['search/inhibitSuggestions']) {
    store.dispatch('navigation/setSearchFocused', true);
    if (query.value.trim()) {
      store.dispatch('search/setShowSuggestionsFlyout', true);
    }
    store.dispatch('search/setQuery', query.value); // make sure suggetions are being loaded
  }
};

const onBlur = () => {
  isFocused.value = false;
  store.dispatch('navigation/setSearchFocused', false);
};

const change = (value: string) => {
  if (query.value !== value) {
    store.dispatch('search/setQuery', value);
  }

  if (value === '') {
    store.commit('navigation/SHOWFLYOUT', false);
    store.dispatch('navigation/showSearchSuggest', false);
    store.commit('search/SET_SEARCH_SUGGESTIONS', { suggestions: [], query: '' });
  }
};

defineExpose({
  focus,
});
</script>

<style scoped src="./search-box-fi" />

<style src="./search-box-fi-unscoped" />
